import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import AurDrawer from '../../common/aur-drawer/AurDrawer';
import AurButton from '../../common/aur-button/AurButton';
import {t} from 'i18next';
import {AurTextField} from '../../common/aur-text-field/AurTextField';
import {useCallback, useEffect, useState} from 'react';
import {AurSwitch} from '../../common/aur-switch/AurSwitch';
import {
  ID_SUFFIX,
  LOAD_DOC_TYPE,
  MAX_CHAR,
  MIN_SEARCH_CHAR,
  QUERY_TYPE,
} from '../../../constants/constants';
import AurLocationTreeView from './AurLocationTreeView';
import {TreeViewBaseItem} from '@mui/x-tree-view/models';
import AurTooltip from '../../common/aur-tooltip/AurTooltip';
import {ReactComponent as InfoIcon} from '../../../../src/assests/svg/info_icon.svg';
import AurPopup from '../../common/aur-popup/AurPopup';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  addNewGroup,
  createSaveSearch,
  getSavedSearchDetails,
  getSavedSearchGroupsRoot,
  savedSearchedRun,
  updateSaveSearchDetails,
} from '../../../api/EcaServices';
import {SavedSearchRes} from '../../eca/SavedSearchPanel';
import {AxiosError} from 'axios';
import {IFilterSet, ISavedSearchReqBody} from '../eca-doc-list.util';
import {defaultFilterModel, defaultFilterSet} from '../EcaDocListGrid';
import {setFilterList} from '../../../redux/slices/ecaDocListSlice';
import {
  extractSavedSearchId,
  getExpandableIds,
  getSavedSearchResFromEntity,
  getTreeObjectFromSavedSearch,
  isEmpty,
  isValidName,
  wrapQueryBeforeSymbol,
} from '../../../utils/utils';
import {
  setEcaDrawerFlag,
  setIsQuerySaved,
} from '../../../redux/slices/ecaAdhocSearchSlice';
import AurSearchBox from '../../common/aur-search-box/AurSearchBox';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_ERROR_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {countRules} from '../../pages/index-search/query-field/query-field-utils';
export type ExtendedTreeItemProps = {
  fileType: string;
  id: string;
  label: string;
  count?: string;
  fieldId?: string;
  newFolder?: boolean;
  createdBy?: string;
  createdDate?: string;
  updatedDate?: string;
  updatedBy?: string;
  parentFolder?: string;
  numberOfChild?: number;
  name?: string;
  value?: string;
  exportId?: number;
  type?: string;
  hideAddFolder?: boolean;
  isScopeSelected?: boolean;
  parentGroupId?: string;
  directory_structure_ids?: string;
  parentTagName?: string;
  displayOrder?: number;
  isDefault?: boolean;
  ecaSearchId?: string;
};

export interface IAddNewGroupReqBody {
  name: string;
  parentGroupId?: string;
}

interface EcaSaveSearchProps {
  setShowDrawer: (value: boolean) => void;
  showDrawer: boolean;
  searchName?: string;
  isPublic?: boolean;
  filterSet?: IFilterSet;
  onCancelClick?: () => void;
}

const EcaSaveSearch = (props: EcaSaveSearchProps) => {
  const {
    setShowDrawer,
    showDrawer,
    searchName = '',
    isPublic = true,
    filterSet,
    onCancelClick,
  } = props;
  const [treeData, setTreeData] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const [name, setName] = useState<string>(searchName);
  const [notes, setNotes] = useState<string>('');
  const [markPublic, setMarkPublic] = useState<boolean>(isPublic);
  const [saveFilters] = useState<boolean>(false);
  const [attentionModal, setAttentionModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [apiLoading, setApiLoading] = useState<boolean>(false);
  const [selectedNode, setSelectedNode] =
    useState<TreeViewBaseItem<ExtendedTreeItemProps> | null>();
  const [disableOverWrite, setDisableOverWrite] = useState<boolean>(true);
  const [parentFolderId, setParentFolderId] = useState<string>('');
  const [value, setValue] = useState<string>('new');
  const [renameSearchError, setRenameSearchError] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [currentExpandedIds, setCurrentExpandedIds] = useState(['']);

  const {showToast} = useToast();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  const {clientId, projectId} = useSelector(
    (state: RootState) => state.reports
  );

  const {index, builder} = useSelector((state: RootState) => state.query);

  const {columnsList, sortOrder, queryData, viewDocName, editDocsName} =
    useSelector((state: RootState) => state.ecaDocList);

  const {globalSavedSearchDetails} = useSelector(
    (state: RootState) => state.ecaAdhocSearch
  );

  const paramsClientProject = `?clientId=${clientId}&projectId=${projectId}`;

  useEffect(() => {
    setLoading(true);
    fetchSaveSearchGroupsRoot(searchKeyword ? {searchTerm: searchKeyword} : {});
    dispatch(setEcaDrawerFlag(false));
  }, [searchKeyword]);

  useEffect(() => {
    if (
      (viewDocName == LOAD_DOC_TYPE.SAVED_DOC || editDocsName) &&
      globalSavedSearchDetails
    ) {
      setValue('overwrite');
      setDisableOverWrite(false);
      setName(globalSavedSearchDetails.name);
      setNotes(globalSavedSearchDetails.notes);
      setMarkPublic(globalSavedSearchDetails.isPublicFlag);
    }
  }, [viewDocName, editDocsName, globalSavedSearchDetails]);

  useEffect(() => {
    if (
      (viewDocName == LOAD_DOC_TYPE.SAVED_DOC || editDocsName) &&
      globalSavedSearchDetails
    ) {
      if (value == 'overwrite' && globalSavedSearchDetails.isPublicFlag) {
        setMarkPublic(globalSavedSearchDetails.isPublicFlag);
      }
    }
  }, [value, markPublic]);

  useEffect(() => {
    if (value == 'new') {
      setName('');
      setNotes('');
      setMarkPublic(true);
      setRenameSearchError(false);
    } else if (globalSavedSearchDetails) {
      setName(globalSavedSearchDetails.name);
      setNotes(globalSavedSearchDetails.notes);
      setMarkPublic(globalSavedSearchDetails.isPublicFlag);
      setRenameSearchError(!isValidName(globalSavedSearchDetails.name));
    }
  }, [value]);

  const fetchSaveSearchGroupsRoot = async (
    params: Record<string, unknown> = {}
  ) => {
    if (clientId && projectId) {
      try {
        const result = await getSavedSearchGroupsRoot({
          clientId,
          projectId,
          ...params,
        });
        const {data: savedSearchData} = result.data;

        if (!isEmpty(savedSearchData)) {
          const resData: SavedSearchRes = getSavedSearchResFromEntity(
            savedSearchData?.[0] ?? {}
          );

          const {groupId} = resData ?? {};

          setParentFolderId(groupId + ID_SUFFIX.SAVED_SEARCH + 0 || '');

          const mappedArray = [resData].map(
            (item: SavedSearchRes, index: number) =>
              getTreeObjectFromSavedSearch(item, index, item?.groupId ?? '')
          );
          setTreeData(mappedArray);

          if (!isEmpty(searchKeyword)) {
            setCurrentExpandedIds(getExpandableIds(mappedArray));
          } else {
            setCurrentExpandedIds(['']);
          }
        } else {
          setTreeData([]);
        }

        setLoading(false);
      } catch (error: unknown) {
        const err = error as AxiosError;
        showToast(err.message, 'error', undefined, 'center');
        setLoading(false);
      }
    }
  };

  const handleSelectedItem = (
    selectedNode: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => {
    setSelectedNode(selectedNode);
  };

  const handleSaveClick = () => {
    if (value == 'overwrite') {
      if (markPublic) {
        setAttentionModal(true);
      } else {
        handleOverWriteSearch();
      }
    } else {
      setApiLoading(true);
      if (selectedNode?.newFolder) {
        const parentGroupId = extractSavedSearchId(selectedNode.parentGroupId);
        createNewGroup(parentGroupId, selectedNode.label);
      } else if (selectedNode?.fileType != 'folder') {
        createSavedSearch(selectedNode?.parentGroupId);
      } else {
        createSavedSearch(selectedNode?.id);
      }
    }
  };

  const handleOverWriteSearch = async () => {
    setAttentionModal(false);
    setApiLoading(true);
    const isEditSavedSearch =
      editDocsName === LOAD_DOC_TYPE.SAVED_SEARCHES_EDIT;
    const reqBody: ISavedSearchReqBody = {
      sortOrder: isEditSavedSearch
        ? globalSavedSearchDetails?.sortOrder || []
        : sortOrder.map((order) => ({
            column: order.column,
            direction: order.direction,
          })),
      scope: queryData.scope
        ? {
            scopeType: queryData.scope.Type,
            scopeId: queryData.scope.Id,
            scopeName: queryData.scope.Name,
          }
        : undefined,
      columnOrder: isEditSavedSearch
        ? globalSavedSearchDetails?.columnOrder || []
        : (columnsList ?? []).map((col) => col.name),
      applyFilter: saveFilters,
      filterSet: isEditSavedSearch
        ? globalSavedSearchDetails?.filterSet || defaultFilterSet
        : saveFilters
          ? filterSet
          : defaultFilterSet,
      name: name,
      notes: notes,
      isPublicFlag: markPublic,
      type: QUERY_TYPE.DT_SEARCH,
      queryBuilderFlag: queryData.queryBuilderMode,
      dtSyntaxQuery: queryData.queryBuilderMode
        ? queryData.dtSyntaxQuery
        : wrapQueryBeforeSymbol(queryData.dtSyntaxQuery),
      noScopeDtSyntaxQuery: queryData.queryBuilderMode
        ? queryData.dtSyntaxQuery
        : wrapQueryBeforeSymbol(queryData?.noScopeDtSyntaxQuery),
      query: queryData.queryBuilderMode
        ? queryData.jsonLogicQuery
        : wrapQueryBeforeSymbol(queryData.dtSyntaxQuery),
      groupId: extractSavedSearchId(globalSavedSearchDetails?.groupId ?? ''),
      relationalGroupFieldList: queryData.relationalGroupFieldList,
      indexName: index.name,
      fieldTemplateId: index.fieldTemplateId,
    };
    if (queryData.queryBuilderMode) {
      reqBody.jsonLogicQuery = JSON.stringify(builder);
    }
    try {
      await updateSaveSearchDetails(
        paramsClientProject,
        globalSavedSearchDetails?.id || '',
        reqBody
      );
      setShowDrawer(false);
      showToast(
        t('ecaDocList.saveSearch.savedSuccess'),
        'success',
        undefined,
        'center'
      );
      if (globalSavedSearchDetails?.applyFilter && !saveFilters) {
        dispatch(setFilterList(defaultFilterModel));
      }
      dispatch(setIsQuerySaved(true));
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t(t('gaTracking.global.save')),
          type: GA_CLICK_TYPES.BUTTON,
          region: GA_REGIONS.DRAWER,
          action: t('gaTracking.queryBuilder.savedSearch'),
          ...(queryData.queryBuilderMode && {
            details: t('gaTracking.queryBuilder.numberOfComponents', {
              length: countRules(builder),
            }),
          }),
        },
        subPage: {
          subpage_name: t('gaTracking.queryBuilder.qbSaveSearch'),
          subpage_type: GA_SUBPAGE_TYPE.DRAWER,
        },
      });
    } catch (error: unknown) {
      const e = error as AxiosError<any>
      const errorMessage = e.response?.data?.meta?.message || e.message 
      showToast(errorMessage, 'error', undefined, 'center', undefined, {
        dontTrack: true,
      });
      queryData.queryBuilderMode &&
        trackGAEvent({
          eventName: GA_EVENT_NAMES.ERROR,
          error: {
            code: e.code,
            type: GA_ERROR_TYPES.VALIDATION_ERROR,
            text: errorMessage,
          },
        });
    } finally {
      setApiLoading(false);
    }
  };

  const createNewGroup = async (parentId: string, name: string) => {
    const reqBody = {
      name: name,
      parentGroupId: extractSavedSearchId(parentId),
    };
    try {
      const results = await addNewGroup(paramsClientProject, reqBody);
      createSavedSearch(results?.data?.groupId);
    } catch (error: unknown) {
      const e = error as AxiosError;
      setApiLoading(false);
      showToast(e.message, 'error', undefined, 'center');
    }
  };

  const getDtSyntaxQuery = () => {
    if (queryData.queryBuilderMode && queryData.dtSyntaxQuery) {
      return queryData.dtSyntaxQuery;
    } else if (queryData.dtSyntaxQuery) {
      return wrapQueryBeforeSymbol(queryData.dtSyntaxQuery);
    } else if (viewDocName == LOAD_DOC_TYPE.ALL_DOC) {
      return '*';
    } else {
      return '';
    }
  };

  const getNoScopeDtSyntaxQuery = () => {
    if (queryData.queryBuilderMode) {
      return getDtSyntaxQuery();
    } else if (viewDocName == LOAD_DOC_TYPE.ALL_DOC) {
      return '*';
    } else {
      return wrapQueryBeforeSymbol(queryData?.noScopeDtSyntaxQuery);
    }
  };

  const createSavedSearch = async (groupId: string | undefined) => {
    const isEditSavedSearch =
      editDocsName === LOAD_DOC_TYPE.SAVED_SEARCHES_EDIT;
    const reqBody: ISavedSearchReqBody = {
      sortOrder: isEditSavedSearch
        ? globalSavedSearchDetails?.sortOrder || []
        : sortOrder.map((order) => ({
            column: order.column,
            direction: order.direction,
          })),
      scope: queryData.scope
        ? {
            scopeType: queryData.scope.Type,
            scopeId: queryData.scope.Id,
            scopeName: queryData.scope.Name,
          }
        : undefined,
      columnOrder: isEditSavedSearch
        ? globalSavedSearchDetails?.columnOrder || []
        : (columnsList ?? []).map((col) => col.name),
      applyFilter: saveFilters,
      filterSet: isEditSavedSearch
        ? globalSavedSearchDetails?.filterSet || defaultFilterSet
        : saveFilters
          ? filterSet
          : defaultFilterSet,
      name: name,
      notes: notes,
      isPublicFlag: markPublic,
      type: QUERY_TYPE.DT_SEARCH,
      queryBuilderFlag: queryData.queryBuilderMode,
      dtSyntaxQuery: getDtSyntaxQuery(),
      noScopeDtSyntaxQuery: getNoScopeDtSyntaxQuery(),
      groupId: groupId
        ? extractSavedSearchId(groupId)
        : extractSavedSearchId(parentFolderId),
      relationalGroupFieldList: queryData.relationalGroupFieldList,
      indexName: index.name,
      fieldTemplateId: index.fieldTemplateId,
    };
    if (queryData.queryBuilderMode) {
      reqBody.jsonLogicQuery = JSON.stringify(builder);
    }
    try {
      const results = await createSaveSearch(paramsClientProject, reqBody);
      if (results.data && results.data.saveSearchId) {
        handleRunSearch(results.data.saveSearchId);
      } else {
        setApiLoading(false);
      }
      queryData.queryBuilderMode &&
        trackGAEvent({
          eventName: GA_EVENT_NAMES.CLICK,
          click: {
            name: t('gaTracking.global.save'),
            type: GA_CLICK_TYPES.BUTTON,
            region: GA_REGIONS.DRAWER,
            action: t('gaTracking.queryBuilder.savedSearch'),
            ...(queryData.queryBuilderMode && {
              details: t('gaTracking.queryBuilder.numberOfComponents', {
                length: countRules(builder),
              }),
            }),
          },
          subPage: {
            subpage_name: t('gaTracking.queryBuilder.qbSaveSearch'),
            subpage_type: GA_SUBPAGE_TYPE.DRAWER,
          },
        });
    } catch (error: unknown) {
      const e = error as AxiosError;
      showToast(e.message, 'error', undefined, 'center', undefined, {
        dontTrack: true,
      });
      queryData.queryBuilderMode &&
        trackGAEvent({
          eventName: GA_EVENT_NAMES.ERROR,
          error: {
            code: e.code,
            type: GA_ERROR_TYPES.VALIDATION_ERROR,
            text: e.message,
          },
        });
      setApiLoading(false);
    }
  };

  const handleRunSearch = async (saveSearchId: string) => {
    try {
      const {data} = await getSavedSearchDetails(
        paramsClientProject,
        saveSearchId
      );
      const ecaSearchId = data?.data?.ecaSearchId;
      if (ecaSearchId) {
        await savedSearchedRun(
          paramsClientProject,
          index.fieldTemplateId,
          ecaSearchId
        );
      }
      // eslint-disable-next-line no-empty
    } catch (error: unknown) {
    } finally {
      setApiLoading(false);
      showToast(
        t('ecaDocList.saveSearch.savedSuccess'),
        'success',
        undefined,
        'center'
      );
      dispatch(setEcaDrawerFlag(true));
      setShowDrawer(false);
      dispatch(setIsQuerySaved(true));
    }
  };

  const PublicToggle = () => {
    return (
      <>
        <div className="public-toggle">
          <AurSwitch
            checked={markPublic}
            onChange={(e) => {
              setMarkPublic(e.target.checked);
            }}
          />
          <div className="public-toggle-title">
            <Typography className="typo-text body2_size row-text">
              {t('ecaDocList.saveSearch.publicToggle')}
            </Typography>
            <span className="tooltip-padding">
              <AurTooltip
                title={t('ecaDocList.saveSearch.publicTooltip')}
                aria-label={t('ecaDocList.saveSearch.publicTooltip')}
                className="typo-text label2_size tooltip-content"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -7],
                        },
                      },
                    ],
                  },
                }}
              >
                <InfoIcon tabIndex={0} />
              </AurTooltip>
            </span>
          </div>
        </div>
      </>
    );
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value);
  };

  const renderSearchBox = useCallback(() => {
    return (
      <Box className="search-container" data-testid="search-container">
        <AurSearchBox
          label={t('common.search')}
          minCharForSearch={MIN_SEARCH_CHAR}
          onSearch={(keyword: string) => {
            setSearchKeyword(keyword.trim());
          }}
          classNameProps="filter-text-field"
        />
      </Box>
    );
  }, []);

  const contentBody = apiLoading ? (
    <AurLoadingIndicator show={true} size={48} className="loader-save-search" />
  ) : (
    <div className="eca-save-search-container" data-testid="save-search-panel">
      <div className="panel-header"></div>
      <div className="panel-title">
        <div className="panel-header-container">
          <div className="panel-header-title">
            <Typography className="typo-text headline1_size custom-header">
              {t('ecaDocList.saveSearch.saveSearch')}
            </Typography>
            <Typography className="typo-text body4_size custom-info">
              {t('ecaDocList.saveSearch.saveSearchText')}
            </Typography>
          </div>
        </div>
        <div className="panel-input-container">
          <div className="save-search-radio-container">
            <FormControl>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={value}
                onChange={handleChange}
                row
              >
                <FormControlLabel
                  value="new"
                  control={<Radio />}
                  label={t('buttons.saveAsNew')}
                  className="typo-text body2_size row-text"
                />
                <FormControlLabel
                  value="overwrite"
                  control={<Radio />}
                  label={t('buttons.overwriteSearch')}
                  className="typo-text body2_size row-text"
                  disabled={disableOverWrite}
                />
              </RadioGroup>
            </FormControl>
          </div>
          <AurTextField
            error={renameSearchError}
            helperText={
              renameSearchError ? t('eca.flyout.savedSearch.nameError') : ''
            }
            onChange={(e) => {
              const newQuery = e.target.value;
              setRenameSearchError(!isValidName(newQuery));
              setName(newQuery);
            }}
            variant="filled"
            className={`aur-textfield`}
            label={t('ecaDocList.saveSearch.nameSearch')}
            placeholder={t('ecaDocList.saveSearch.nameSearchText')}
            value={name}
            inputProps={{
              maxLength: 50,
            }}
            ariaLabel={t('ecaDocList.saveSearch.nameSearch')}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <div className="panel-toggle-container">
            <PublicToggle />
          </div>
          <div>
            <AurTextField
              onChange={(e) => {
                const newQuery = e.target.value;
                setNotes(newQuery);
              }}
              variant="filled"
              className={`aur-textfield`}
              label={t('ecaDocList.saveSearch.noteSearch')}
              placeholder={t('ecaDocList.saveSearch.noteSearchText')}
              value={notes}
              ariaLabel={t('ecaDocList.saveSearch.noteSearch')}
              inputProps={{
                maxLength: MAX_CHAR,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              multiline
              sx={{
                '& .MuiInputBase-root': {
                  resize: 'both',
                  overflow: 'auto',
                  display: 'flex',
                  alignItems: 'flex-start',
                },
              }}
            />
            <Typography className="typo-text label2_size custom-text-style">
              {t('ecaDocList.saveSearch.noteValidationMsg')}
            </Typography>
          </div>
        </div>
      </div>

      <div className={`location-container ${value != 'new' ? 'hidden' : ''}`}>
        <div className="location-header">
          <div>
            <Typography className="typo-text label2_size custom-text-style-location">
              {t('ecaDocList.saveSearch.location')}
            </Typography>
          </div>
          <div>
            <Typography className="typo-text body3_size custom-text-style">
              {t('ecaDocList.saveSearch.contactAdmin')}
            </Typography>
          </div>
        </div>
        <div className="location-body">
          <div className="location-content">
            {renderSearchBox()}
            <div className="location-treeview-container">
              <div className="location-treeview-content">
                {loading ? (
                  <AurLoadingIndicator
                    show={true}
                    size={32}
                    className="loader-save-search-control"
                  />
                ) : (
                  <AurLocationTreeView
                    itemsList={treeData}
                    addFolderAllowed={true}
                    defaultExpandedIds={currentExpandedIds}
                    handleSelectedItem={handleSelectedItem}
                    showTooltip={true}
                    allowGlobalSelect={true}
                    selectedItem={
                      globalSavedSearchDetails
                        ? globalSavedSearchDetails?.groupId
                        : parentFolderId
                    }
                    isFromEcaSaveSearch={true}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="panel-footer">
        <div className="save-btn-conatiner">
          <div>
            <AurButton
              classNames={'drawer-close-button panel-close'}
              label={t('buttons.cancel')}
              type="exit"
              size="Medium"
              title={t('buttons.cancel')}
              dataTestId="cancel-btn"
              onClick={() => {
                setShowDrawer(false);
                onCancelClick?.();
              }}
            />
          </div>
          <AurButton
            classNames={'drawer-close-button panel-close save-btn'}
            label={t('buttons.save')}
            type="continue"
            size="Medium"
            title={t('buttons.save')}
            onClick={handleSaveClick}
            disabled={!name.trim() || renameSearchError}
          />
        </div>
      </div>
    </div>
  );
  return (
    <>
      <AurDrawer
        className="eca-saved-search-drawer"
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        anchor="right"
        drawerContent={contentBody}
        drawerRootSx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      />

      {attentionModal && (
        <AurPopup
          className="aur-overwrite-popup"
          open={attentionModal}
          onOk={handleOverWriteSearch}
          onCancel={() => setAttentionModal(false)}
          popupTitle={t('common.justToConfirm')}
          popupHeading={t('ecaDocList.saveSearch.overwriteHeader')}
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={false}
          popupContent={t('ecaDocList.saveSearch.overwriteContent')}
          labelActionCancel={t('buttons.cancel')}
          cancelButtonTitle={t('buttons.cancel')}
          labelActionOk={t('buttons.overwriteSearchBtn')}
          exitButtonHint={t('buttons.cancel')}
          continueButtonHint={t('buttons.overwriteSearchBtn')}
        />
      )}
    </>
  );
};

export default EcaSaveSearch;
